import {useEffect, useId, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {Copy, Send, X} from "lucide-react";
import {useTranslation} from "../../../shared/i18n";
import {formatDateTime} from "../../../shared/utils/format";
import {createBotInvite, mapPartnerBotApiError} from "../partnerBotApi";
import "../../../shared/finance/financeOperations.css";

const focusableElements = (container) => Array.from(container.querySelectorAll(
    "button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])",
));

const EXPIRES_OPTIONS = [
    {value: 60, labelKey: "orgs.partnerBot.expires.hour"},
    {value: 1440, labelKey: "orgs.partnerBot.expires.day"},
    {value: 10080, labelKey: "orgs.partnerBot.expires.week"},
];

export const PartnerBotInviteDialog = ({open, companyId, entity, canInviteOwner, onCancel, onCreated}) => {
    const {t} = useTranslation();
    const titleId = useId();
    const dialogRef = useRef(null);
    const firstFieldRef = useRef(null);
    const invokingElementRef = useRef(null);
    const submittingRef = useRef(false);
    const [role, setRole] = useState("manager");
    const [expiresInMinutes, setExpiresInMinutes] = useState(1440);
    const [reason, setReason] = useState("");
    const [pending, setPending] = useState(false);
    const [error, setError] = useState(null);
    // deep_link держим только в локальном стейте: не в queryClient, не в URL,
    // без логов — ссылка одноразовая и равна доступу в кабинет партнёра.
    const [issued, setIssued] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        setRole("manager");
        setExpiresInMinutes(1440);
        setReason("");
        setError(null);
        setIssued(null);
        setCopied(false);
        setPending(false);
        submittingRef.current = false;
    }, [open, companyId]);

    useEffect(() => {
        if (!open) return undefined;
        invokingElementRef.current = document.activeElement;
        const frame = window.requestAnimationFrame(() => firstFieldRef.current?.focus());
        return () => {
            window.cancelAnimationFrame(frame);
            const invokingElement = invokingElementRef.current;
            if (invokingElement && document.contains(invokingElement)) invokingElement.focus();
            invokingElementRef.current = null;
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                if (!submittingRef.current) onCancel();
                return;
            }
            if (event.key !== "Tab" || !dialogRef.current) return;
            const focusable = focusableElements(dialogRef.current);
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [onCancel, open]);

    if (!open) return null;

    const submit = async (event) => {
        event.preventDefault();
        if (submittingRef.current) return;
        const trimmedReason = reason.trim();
        if (trimmedReason.length < 3) {
            setError({message: t("orgs.common.reasonTooShort")});
            return;
        }

        submittingRef.current = true;
        setPending(true);
        setError(null);
        try {
            const response = await createBotInvite({
                companyId,
                role,
                expiresInMinutes: Number(expiresInMinutes),
                reason: trimmedReason,
            });
            setIssued(response);
            onCreated?.();
        } catch (requestError) {
            setError(mapPartnerBotApiError(requestError));
        } finally {
            submittingRef.current = false;
            setPending(false);
        }
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(issued.deep_link);
            setCopied(true);
        } catch (_copyError) {
            setCopied(false);
        }
    };

    return createPortal(
        <div className="finance-dialog-backdrop">
            <section
                ref={dialogRef}
                className="finance-dialog"
                role="dialog"
                aria-modal="true"
                aria-busy={pending}
                aria-labelledby={titleId}
            >
                <form onSubmit={submit}>
                    <header className="finance-dialog-header">
                        <div>
                            <p className="finance-dialog-entity">{entity}</p>
                            <h2 id={titleId}>{t("orgs.partnerBot.inviteTitle")}</h2>
                        </div>
                        <button
                            type="button"
                            className="finance-icon-button"
                            aria-label={t("orgs.common.close")}
                            title={t("orgs.common.close")}
                            disabled={pending}
                            onClick={onCancel}
                        >
                            <X aria-hidden="true" size={18} />
                        </button>
                    </header>

                    <p>{t("orgs.partnerBot.inviteCopy")}</p>

                    <div aria-live="polite">
                        {issued ? (
                            <div className="support-code-result">
                                <p className="partner-bot-link-value">{issued.deep_link}</p>
                                <p className="admin-card-note">
                                    {t("orgs.partnerBot.linkUntil", {date: formatDateTime(issued.expires_at)})}
                                </p>
                                <button type="button" className="admin-button admin-button-secondary" onClick={copyLink}>
                                    <Copy aria-hidden="true" size={15} /> {t("orgs.partnerBot.copyLink")}
                                </button>
                                {copied ? <span className="admin-card-note">{t("orgs.partnerBot.copied")}</span> : null}
                            </div>
                        ) : null}
                    </div>

                    {!issued ? (
                        <div className="finance-dialog-fields">
                            <label>
                                <span>{t("orgs.common.role")}</span>
                                <select
                                    ref={firstFieldRef}
                                    className="partner-bot-select"
                                    value={role}
                                    disabled={pending}
                                    onChange={(event) => {
                                        setRole(event.target.value);
                                        setError(null);
                                    }}
                                >
                                    {canInviteOwner ? <option value="owner">{t("orgs.partnerBot.roles.owner")}</option> : null}
                                    <option value="manager">{t("orgs.partnerBot.roles.manager")}</option>
                                    <option value="accountant">{t("orgs.partnerBot.roles.accountant")}</option>
                                </select>
                            </label>
                            <label>
                                <span>{t("orgs.partnerBot.ttl")}</span>
                                <select
                                    className="partner-bot-select"
                                    value={expiresInMinutes}
                                    disabled={pending}
                                    onChange={(event) => {
                                        setExpiresInMinutes(Number(event.target.value));
                                        setError(null);
                                    }}
                                >
                                    {EXPIRES_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                                    ))}
                                </select>
                            </label>
                            <label>
                                <span>{t("orgs.common.reason")}</span>
                                <input
                                    value={reason}
                                    disabled={pending}
                                    autoComplete="off"
                                    onChange={(event) => {
                                        setReason(event.target.value);
                                        setError(null);
                                    }}
                                />
                            </label>
                        </div>
                    ) : null}

                    {error?.message ? <div className="finance-dialog-error" role="alert">{error.message}</div> : null}

                    <footer className="finance-dialog-footer">
                        <button type="button" className="admin-button admin-button-secondary" disabled={pending} onClick={onCancel}>
                            {issued ? t("orgs.partnerBot.done") : t("orgs.common.cancel")}
                        </button>
                        {!issued ? (
                            <button type="submit" className="admin-button admin-button-primary" disabled={pending}>
                                <Send aria-hidden="true" size={15} /> {pending ? t("orgs.partnerBot.issuing") : t("orgs.partnerBot.issue")}
                            </button>
                        ) : null}
                    </footer>
                </form>
            </section>
        </div>,
        document.body,
    );
};
